<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Which notifications reach me, on which channel, and with which figures?"
  Answer:   the table itself - one row per type, one column per legal channel.
  Context:  the mandatory/customized badge on each cell explaining why it looks the way it does.
  Controls: each cell's own checkbox, plus the two show-amounts switches above the table.
-->
<script setup lang="ts">
import { ErrorState, PageHeader, PageShell, StatusTag } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSession } from '@/app/session'
import {
  isChannelNotSupported,
  isTypeMandatory,
  NotificationPreferencesTable,
  problemDetail,
  useNotificationPreferences,
} from '@/features/notifications'

const { t } = useI18n()
const session = useSession()

const ledgerId = () => session.activeLedger.value?.id
const preferences = useNotificationPreferences({ ledgerId })

const saveErrorMessage = computed(() => {
  const error = preferences.saveError.value
  if (error === null) return undefined
  if (isTypeMandatory(error)) return t('notificationPreferences.errorTypeMandatory')
  if (isChannelNotSupported(error)) return t('notificationPreferences.errorChannelNotSupported')
  return problemDetail(error) ?? t('notificationPreferences.errorGeneric')
})
</script>

<template>
  <PageShell class="gap-[var(--space-section)]">
    <PageHeader :title="t('notificationPreferences.title')">
      <template #subtitle>{{ t('notificationPreferences.subtitle') }}</template>
    </PageHeader>

    <p
      v-if="
        session.isLoading.value || (session.activeLedger.value !== undefined && preferences.isLoading.value)
      "
      role="status"
    >
      {{ t('app.loading') }}
    </p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('entries.noLedger') }}
    </p>
    <ErrorState
      v-else-if="preferences.error.value"
      :title="t('notificationPreferences.errorTitle')"
      :body="t('notificationPreferences.errorLoading')"
      :retry-label="t('notifications.retry')"
      :retrying="preferences.retrying.value"
      @retry="preferences.retry()"
    />

    <div v-else-if="preferences.preferences.value" class="flex flex-col gap-[var(--space-block)]">
      <NotificationPreferencesTable
        :preferences="preferences.preferences.value"
        :is-saving="preferences.isSaving.value"
        @toggle-channel="(type, channel, enabled) => preferences.setChannelEnabled(type, channel, enabled)"
        @toggle-show-amounts="(channel, enabled) => preferences.setShowAmounts(channel, enabled)"
      />
      <StatusTag v-if="saveErrorMessage" tone="danger">{{ saveErrorMessage }}</StatusTag>
    </div>
  </PageShell>
</template>
